import { z } from "zod";
import { Timestamp } from "./primitives.js";
/**
 * @fileoverview The owner's own writing: theses, excerpts, notes and open questions.
 *
 * Nothing automated modifies, summarises or reorganises it. Bodies are stored exactly as
 * sent, so these schemas do not trim or normalise them.
 */
/** Markdown as the owner typed it. Bounded only to keep a runaway paste out of the row. */
const Markdown = z.string().max(200_000);
/**
 * One running argument per situation. Every `PUT` snapshots a version, unconditionally.
 */
export const Thesis = z.object({
    situationId: z.string().uuid(),
    bodyMd: Markdown,
    updatedAt: Timestamp,
    /** How many snapshots exist, so the UI can offer history without fetching it. */
    versionCount: z.number().int().nonnegative(),
});
/** The body of a thesis save: the whole new text, which replaces the current one. */
export const ThesisPut = z.object({ bodyMd: Markdown });
/** One saved snapshot of a thesis. */
export const ThesisVersion = z.object({
    id: z.string().uuid(),
    savedAt: Timestamp,
    /** Present only when one version is requested; the list omits bodies. */
    bodyMd: Markdown.optional(),
    /** Characters added and removed against the previous version, for the history list. */
    charDelta: z.number().int(),
});
/**
 * A clipped passage, stored with its exact source location. `POST` rejects a quote that
 * cannot be located in the document it cites.
 */
export const ExcerptCreate = z.object({
    documentId: z.string().uuid(),
    charStart: z.number().int().nonnegative(),
    charEnd: z.number().int().positive(),
    quote: z.string().min(1).max(20_000),
    comment: Markdown.optional(),
});
/** Only the owner's own commentary is editable. The quote and its offsets are not. */
export const ExcerptPatch = z.object({ comment: Markdown.nullable() });
/** A stored excerpt, with its source location and the owner's comment on it. */
export const Excerpt = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid(),
    documentId: z.string().uuid(),
    charStart: z.number().int(),
    charEnd: z.number().int(),
    quote: z.string(),
    comment: z.string().nullable(),
    createdAt: Timestamp,
    /** Enough to render a citation without a second request. */
    document: z
        .object({
        accession: z.string(),
        formType: z.string().nullable(),
        filename: z.string(),
    })
        .nullable(),
});
/**
 * Free-form, attachable to a situation, a filing or a company — the three things the
 * owner looks at. `subjectId` is text rather than uuid because those three are keyed
 * differently: a situation by uuid, a filing by accession, a company by CIK.
 */
export const NoteSubject = z.enum(["situation", "filing", "company"]);
/** A new note on a subject; `subjectId` is keyed as `NoteSubject` describes. */
export const NoteCreate = z.object({
    subjectType: NoteSubject,
    subjectId: z.string().min(1).max(64),
    bodyMd: Markdown,
});
/** A note edit: the whole new body, which replaces the old one. */
export const NotePatch = z.object({ bodyMd: Markdown });
/** A stored note on a situation, a filing or a company. */
export const Note = z.object({
    id: z.string().uuid(),
    subjectType: NoteSubject,
    subjectId: z.string(),
    bodyMd: z.string(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
});
/**
 * A tracked unknown: the one place the record holds what is not known.
 *
 * Resolving one keeps it: `resolvedAt` is set and `resolutionMd` records the answer,
 * rather than the row being deleted.
 */
export const QuestionCreate = z.object({ body: z.string().min(1).max(4_000) });
/** An edit to an open question: its text, its resolution, or both. */
export const QuestionPatch = z.object({
    body: z.string().min(1).max(4_000).optional(),
    /** Null reopens a resolved question; a string resolves it with that answer. */
    resolutionMd: Markdown.nullable().optional(),
});
/** A stored open question; `resolvedAt` is null while it is unresolved. */
export const OpenQuestion = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid(),
    body: z.string(),
    resolvedAt: Timestamp.nullable(),
    resolutionMd: z.string().nullable(),
    createdAt: Timestamp,
});
//# sourceMappingURL=workspace.js.map